import { useMemo } from "react";
import { mergeProps } from "../../utils/mergeProps.es.js";
import { useMenuTriggerState, useMenuTrigger } from "../Overlays.es.js";
import { getItemId } from "./EzListBox.es.js";
import { filterDOMProps } from "./filterDOMProps.es.js";
import { ListKeyboardDelegate } from "./KeyboardDelegate.es.js";
import { useListState } from "./useListState.es.js";
import { useSelectableCollection } from "./useSelectableCollection.es.js";
import { useTypeSelect } from "./useTypeSelect.es.js";
import useOnClickOutside from "../../utils/hooks/useOnClickOutside.es.js";
import useOnFocusOutside from "../../utils/hooks/useOnFocusOutside.es.js";
function useSelectState(props) {
  const triggerState = useMenuTriggerState();
  const listState = useListState({
    ...props,
    onSelectionChange: (key) => {
      const item = listState.collection.index.get(key);
      props.onSelectionChange(item.value);
      triggerState.close();
    }
  });
  return {
    ...triggerState,
    ...listState
  };
}
function useSelect(props, state) {
  var _a;
  const delegate = useMemo(
    () => props.keyboardDelegate || new ListKeyboardDelegate(state.collection),
    [props.keyboardDelegate, state.collection]
  );
  const { selectionManager } = state;
  const { typeSelectProps } = useTypeSelect({
    keyboardDelegate: delegate,
    selectionManager,
    onTypeSelect(key) {
      if (state.isOpen) return;
      selectionManager.replaceSelection(key);
    }
  });
  const { menuTriggerProps, menuProps } = useMenuTrigger(state);
  const { collectionProps } = useSelectableCollection({ ...state, keyboardDelegate: delegate });
  const inputProps = {
    onKeyDown(e) {
      if (!state.isOpen || e.defaultPrevented) return;
      collectionProps.onKeyDown(e);
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        selectionManager.replaceSelection(selectionManager.focusedKey);
      }
    },
    onSelect: (e) => e.target.setSelectionRange(0, 0),
    value: ((_a = state.collection.index.get(selectionManager.selectedKey)) == null ? void 0 : _a.label) || "",
    readOnly: true
  };
  const domProps = filterDOMProps(props, {
    propNames: /* @__PURE__ */ new Set(["id", "name", "disabled", "placeholder", "onFocus"])
  });
  const focusedKeyId = getItemId(menuProps.id, selectionManager.focusedKey);
  const onFocusLost = (e) => {
    var _a2;
    if (!state.isOpen) return;
    state.close();
    (_a2 = props.onBlur) == null ? void 0 : _a2.call(props, e);
  };
  useOnClickOutside(onFocusLost, [props.triggerRef, props.popoverRef]);
  useOnFocusOutside(onFocusLost, [props.triggerRef, props.popoverRef]);
  return {
    inputProps: {
      ...mergeProps(typeSelectProps, menuTriggerProps, inputProps, domProps),
      role: "combobox",
      "aria-controls": menuProps.id,
      "aria-autocomplete": "list",
      "aria-activedescendant": state.isOpen ? focusedKeyId : void 0
    },
    listBoxProps: menuProps,
    keyboardDelegate: delegate
  };
}
export {
  useSelect,
  useSelectState
};
//# sourceMappingURL=useSelect.es.js.map
